import React, {useEffect, useState, useRef, useMemo} from 'react'
import {observer} from 'mobx-react-lite'
import {TObservablePage} from '../../types/commerce'
import {Layout} from '../../components/layout'
import {Container, Stack} from '../../vanilla'
import {flowResult} from 'mobx'
import {
  useBasketStore,
  useContentStore,
  useCustomerStore,
  useOrderStore,
  useRapidDeliveryStore,
  useMarketingPreferencesStore,
  useGlobalStore
} from '../../store/hooks/useStore'
import {Slot, useContentToDisplay} from '../../components/cms/Slot'
import {SlotContentItem, SlotItem} from '../../types/cms'
import {Seo} from '../../components/seo'
import {BookDelivery} from '../../components/book-delivery'
import {EditOrderStickyBanner} from '../../components/edit-order-sticky-banner'
import {usePageDataTracking} from '../../analytics/page-tracking'
import {
  usePromotionDataTracking,
  useRegisterPromotionsForTracking
} from '../../analytics/promotion-tracking'
import {HomePageType} from '../../types/cms/navigation'
import {CONTENT_SCHEMAS} from '../../components/cms/utils'
import {MarketingPreferencesModal} from '../../components/marketing-preferences-modal'
import Banner from '../../components/banner'
import {useBannerTracking} from '../../analytics/banner-tracking'
import {DisplaySlots} from '../category/partials/display-slots'
import {useIntersectionObserver} from '../../hooks/use-intersection-observer'
import { buildHomepageStructuredData, buildWebsiteStructuredData } from '../../utils/structured-data'
import {isMarketingReminderSuppressedForSession} from '../../utils/marketing-preferences-reminder'
import {LOCALSTORAGE_KEYS} from '../../utils/constants'
import {isSuppressedSlotSchema} from '../../utils/rapid-delivery'

interface HomePageProps {
  content?: HomePageType
  children?: React.ReactNode
}

const Home: TObservablePage<HomePageProps> = observer(({content: visualizationContent}) => {
  const contentStore = useContentStore()
  const {editMode, recentOrder} = useOrderStore()
  const {currentDeliverySlotId, postcodeError, openProductUnavailableModal} = useBasketStore()
  const {isRapidDeliveryBooked, rapidDeliveryBlockedSchemas} = useRapidDeliveryStore()
  const {isRegistered} = useCustomerStore()
  const content = visualizationContent ?? contentStore.contentById['homepage']

  const deliveryBanner = content?.showDeliveryBanner?.showBanner
    ? content.showDeliveryBanner.bannerSlot?.[0]
    : undefined

  const [showBanner, setShowBanner] = useState(false)
  const [communicationPreferencesBannerFrequency, setCommunicationPreferencesBannerFrequency] =
    useState<number | null>(null)
  const {
    fetchCommunicationPreferences,
    communicationPreferencesError: error,
    preferenceInExpiryWindow
  } = useMarketingPreferencesStore()
  const {isCustomSitePreferenceSet, getCustomSitePreferenceById, customSitePreferences} =
    useGlobalStore()
  const sendBannerTrackingData = useBannerTracking({
    bannerId: 'preference_reminder',
    bannerName: 'Your marketing preferences',
    bannerType: 'account_notice',
    bannerSlot: 'navigation_notification_bar',
    bannerFormat: 'alert_banner',
    bannerTriggerStatus: 'marketing_preference_expiring_soon'
  })

  const sendPageData = usePageDataTracking()
  const pageType = 'storefront'
  let pageCategory = ''

  if (isRegistered) {
    pageCategory = 'Full-login'
  } else {
    pageCategory = 'Default'
  }
  const sendPromotionData = usePromotionDataTracking()
  const registerPromotions = useRegisterPromotionsForTracking()
  const heroRef = useRef<HTMLDivElement | null>(null)
  const {isOnScreen: isHeroInView} = useIntersectionObserver(heroRef, {
    useOnce: true,
    rootMargin: '-70px 0px -70px 0px'
  })

  const heroSlotGroups = content?.slots
    // Add index position of slots array for tracking purposes
    .map((slotGroup: SlotContentItem, index: number) => ({ slotGroup, originalIndex: index }))
    .filter(({slotGroup}: {slotGroup: SlotContentItem; originalIndex: number}) =>
      slotGroup._meta.schema === CONTENT_SCHEMAS.HOME_HERO
    ) || []

  const heroSlots = heroSlotGroups
    .map(({ slotGroup, originalIndex }: { slotGroup: SlotContentItem; originalIndex: number }) => {
      const transformedSlots = useContentToDisplay(
        slotGroup,
        isRapidDeliveryBooked && isSuppressedSlotSchema(slotGroup._meta?.schema, rapidDeliveryBlockedSchemas),
      )
      return transformedSlots?.map((slot: SlotItem) => ({
        ...slot,
        slotGroupName: slotGroup._meta.name,
        slotIndex: originalIndex + 1
      })) || []
    }) || []

  let otherSlots = content?.slots
    // Add index position of slots array for tracking purposes
    .map((slotGroup: SlotContentItem, index: number) => ({ slotGroup, originalIndex: index }))
    .filter(({ slotGroup }: { slotGroup: SlotContentItem; originalIndex: number }) => slotGroup._meta.schema !== CONTENT_SCHEMAS.HOME_HERO)
    .map(({ slotGroup, originalIndex }: { slotGroup: SlotContentItem; originalIndex: number }) => {
      const transformedSlots = useContentToDisplay(
        slotGroup,
        isRapidDeliveryBooked && isSuppressedSlotSchema(slotGroup._meta?.schema, rapidDeliveryBlockedSchemas),
      )
      return transformedSlots?.map((slot: SlotItem) => ({
        ...slot,
        slotGroupName: slotGroup._meta.name,
        })) || []
    }).filter((slotGroup: SlotContentItem) => slotGroup.length > 0) || []

  otherSlots = [...otherSlots].map((slotGroup, groupIndex) =>
    slotGroup.map((slot: SlotContentItem) => ({
      ...slot,
      slotIndex: heroSlots.length + groupIndex + 1
    }))
  )

  useEffect(() => {
    sendPageData(pageType, pageCategory)
  }, [isRegistered])

  useEffect(() => {
    registerPromotions(otherSlots)
  }, [])

  useEffect(() => {
    if (isHeroInView && heroSlots.length > 0) {
      sendPromotionData(heroSlots)
    }
  }, [isHeroInView])

  const structuredData = useMemo(() => {
    return [buildHomepageStructuredData(), buildWebsiteStructuredData()]
  }, [])

  useEffect(() => {
    if (isRegistered) {
      fetchCommunicationPreferences()
    }
  }, [isRegistered])

  useEffect(() => {
    (async function () {
      if (!isCustomSitePreferenceSet('communicationPreferencesBannerFrequency')) {
        await flowResult(getCustomSitePreferenceById('communicationPreferencesBannerFrequency'))
      }

      setCommunicationPreferencesBannerFrequency(
        customSitePreferences['communicationPreferencesBannerFrequency'] as number
      )
    })()
  }, [])

  useEffect(() => {
    if (!preferenceInExpiryWindow || communicationPreferencesBannerFrequency === null) {
      return
    }

    if (isMarketingReminderSuppressedForSession()) {
      setShowBanner(false)
      return
    }

    const dismissedAt =
      typeof localStorage !== 'undefined'
        ? localStorage.getItem(LOCALSTORAGE_KEYS.MARKETING_BANNER_DISMISSED_KEY)
        : null
    const hasBannerBeenDismissed =
      dismissedAt !== null &&
      Date.now() - parseInt(dismissedAt, 10) <
        communicationPreferencesBannerFrequency * 24 * 60 * 60 * 1000
    if (!error && preferenceInExpiryWindow && !hasBannerBeenDismissed) {
      setShowBanner(true)
    }
  }, [error, preferenceInExpiryWindow, communicationPreferencesBannerFrequency])

  useEffect(() => {
    if (showBanner) {
      sendBannerTrackingData('alert_banner_view')
    }
  }, [showBanner])

  if (!content) {
    return null
  }

  const dismissBanner = () => {
    setShowBanner(false)
    localStorage.setItem(LOCALSTORAGE_KEYS.MARKETING_BANNER_DISMISSED_KEY, Date.now().toString())
    sendBannerTrackingData('alert_banner_dismiss', undefined, 'dismissed')
  }

  const handleBannerCTA = () => {
    sendBannerTrackingData('alert_banner_click', 'Refresh Now', 'confirmed')
  }

  const bannerContent = {
    text: {
      title: 'Your marketing preferences are due a refresh',
      description:
        "Please update them now to make sure you don't miss out on exclusive deals and offers"
    },
    cta: {
      ctaText: 'Refresh Now',
      ctaLink: '/account/communication-preferences',
      ctaButtonColor: {
        color: 'action',
        name: 'Primary'
      },
      ctaTextColor: {
        color: '#ffffff',
        name: 'White'
      }
    }
  }

  return (
    <Layout>
      {editMode && <EditOrderStickyBanner />}
      <Seo
        title={content?.SEO?.pageTitle || 'Home Page | Iceland'}
        description={content?.SEO?.pageDescription}
        structuredData={structuredData}
      />
      {isRegistered && showBanner && (
        <Banner content={bannerContent} onCTAClicked={handleBannerCTA} onDismiss={dismissBanner} />
      )}
      <Stack height={['260px', '260px', '320px']} ref={heroRef}>
        {heroSlotGroups.map(({slotGroup, originalIndex}: {slotGroup: SlotContentItem; originalIndex: number}) => (
          <Slot
            key={`${slotGroup?._meta?.deliveryId}-${originalIndex}`}
            data={slotGroup}
            suppressDefaultContent={isSuppressedSlotSchema(slotGroup._meta?.schema, rapidDeliveryBlockedSchemas)}
          />
        ))}
      </Stack>
      <Stack
        overflow={deliveryBanner ? 'visible' : 'auto'}
        height={
          deliveryBanner 
            ? 'auto'
            : currentDeliverySlotId
            ? ['292px', '122px']
            : recentOrder
            ? ['196px', '96px']
            : postcodeError
            ? ['114px', '122px']
            : ['auto', '96px']
        }
      >
        <BookDelivery deliveryBanner={deliveryBanner} />
        <MarketingPreferencesModal />
      </Stack>
      <Container paddingBottom={['32px', '48px']}>
        <Stack spacing={['16px', '24px']}>
          {content?.slots
            .filter(
              (slotGroup: SlotContentItem) => slotGroup._meta.schema !== CONTENT_SCHEMAS.HOME_HERO
            )
            .map((slotGroup: SlotContentItem, idx: number) => (
              <DisplaySlots
                key={`${slotGroup?._meta?.deliveryId}-${idx}`}
                slots={slotGroup}
                slotIndex={heroSlots.length + idx + 1}
                slotGroupName={slotGroup._meta?.name}
                suppressDefaultContent={isSuppressedSlotSchema(slotGroup._meta?.schema, rapidDeliveryBlockedSchemas)}
              />
            ))}
        </Stack>
      </Container>
    </Layout>
  )
})

Home.getProps = async ({store}) => {
  const {contentStore} = store
  const {fetchItemByKey} = contentStore
  await flowResult(fetchItemByKey('homepage'))
}

export default Home
